import { computed, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TableOperationsHelper } from '@shared/helpers';
import { TableColumn } from '@ui-kit/table/_core';
import { FeeWaiversHelper } from '../helpers';
import { feeWaiversTableConfig } from '../misc';
import { FeeWaiverRecord, FeeWaiversOperations, FeeWaiversTableColumnEnum } from '../types';
import { FeeWaiversQPService } from './fee-waivers-query-params.service';

@Injectable({ providedIn: 'root' })
export class FeeWaiversOperationsService {
    private route = inject(ActivatedRoute);
    private feeWaiversQueryParamsService = inject(FeeWaiversQPService);

    private _operations = signal<FeeWaiversOperations>(FeeWaiversHelper.getSystemDefaultOperations());
    operations = computed(() => this._operations());

    private _tableData = signal<FeeWaiverRecord[]>([]);
    tableData = computed(() => this._tableData());

    private _totalCount = signal<number>(0);
    totalCount = computed(() => this._totalCount());

    private _filteredCount = signal<number>(0);
    filteredCount = computed(() => this._filteredCount());

    initializeOperations(): void {
        const snapshot = this.route.snapshot;
        const queryParams = { ...snapshot.queryParams };

        if (queryParams && Object.keys(queryParams).length > 0) {
            this.initializeOperationsFromQueryParams(queryParams);
            return;
        }

        this.initializeSystemDefaultOperations();
    }

    initializeOperationsFromQueryParams(queryParams: object): void {
        const operations = this.feeWaiversQueryParamsService.getOperationsFromQueryParams(
            queryParams,
            this.operations()
        );
        this._operations.set(operations);
    }

    initializeSystemDefaultOperations(): void {
        this._operations.set(FeeWaiversHelper.getSystemDefaultOperations());
    }

    setPagination(skip: number, take: number): void {
        const updatedPagination = TableOperationsHelper.computePagination(
            this.operations().pagination.take,
            take,
            skip
        );

        this._operations.update(o => ({
            ...o,
            pagination: updatedPagination,
            skipDataReload: false,
        }));

        this.feeWaiversQueryParamsService.setPaginationQueryParams(updatedPagination);
    }

    setSearchValue(value: string): void {
        this._operations.update(o => ({
            ...o,
            pagination: {
                ...o.pagination,
                skip: 0,
            },
            searchValue: value,
            skipDataReload: false,
        }));

        this.feeWaiversQueryParamsService.setSearchQueryParams(
            this.operations().searchValue,
            this.operations().pagination
        );
    }

    setTableColumns(columns: TableColumn<FeeWaiversTableColumnEnum>[]): void {
        this._operations.update(o => ({
            ...o,
            columns: [...columns],
            skipDataReload: true,
        }));

        this.feeWaiversQueryParamsService.setColumnsQueryPrams(columns);
    }

    resetDisplayedColumns(): void {
        const defaultColumns = feeWaiversTableConfig().columns;
        const updatedColumns = TableOperationsHelper.resetTableColumns(this.operations().columns, defaultColumns);

        this._operations.update(o => ({
            ...o,
            columns: [...updatedColumns],
            skipDataReload: true,
        }));

        this.feeWaiversQueryParamsService.setColumnsQueryPrams(this.operations().columns);
    }

    setTableData(tableData: FeeWaiverRecord[]): void {
        this._tableData.set(tableData);
    }

    setTotalCount(totalCount: number): void {
        this._totalCount.set(totalCount);
    }

    setFilteredCount(filteredCount: number): void {
        this._filteredCount.set(filteredCount);
    }
}
